<template>
  <vs-wrapper space="base">
    <vs-heading type="overline" class="pt-2 pb-2">Activity</vs-heading>
    <vs-text v-if="isHistoryError" class="font-italic">Full job history not loaded</vs-text>
    <vs-snackbar
      :value="isCommentSubmitError"
      type="error"
      sticky
      @input="isCommentSubmitError = false"
    >
      {{ snackbarMessages.failedToSubmitComment }}
    </vs-snackbar>
    <v-timeline class="pt-3" dense>
      <TimelineCommentBox
        v-model="commentDraft"
        class="day-container"
        :loading="isCreatingComment"
        @submit="submitComment"
      />
    </v-timeline>
    <job-history-day-section
      v-for="(changes, date) in jobHistory"
      :key="date"
      class="day-container"
      :date="date"
      :changes="changes"
      :job-start="props.startTime"
      :job-end="props.endTime"
      :job-hub-id="props.jobHubId"
      :custom-field-definitions="props.customFieldDefinitions"
    />
  </vs-wrapper>
</template>

<script setup lang="ts">
import moment from 'moment'

import JobHistoryDaySection from './JobHistoryDaySection.vue'
import TimelineCommentBox from '@/components/common/timeline/TimelineCommentBox.vue'
import { useActivityLog } from './composables/useActivityLog'
import { CustomFieldRecord } from '@/models/types/custom-field'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { useCreateJobCommentMutation } from '@/api/jobs'
import { ref } from 'vue'
import { snackbarMessages } from '@/components/tickets/SnackbarMessages'

interface Props {
  jobId: string
  jobNumber: number
  jobHubId: string
  startTime?: moment.Moment | null
  endTime?: moment.Moment | null
  customFieldDefinitions?: CustomFieldRecord
}
const props: Props = withDefaults(defineProps<Props>(), {
  startTime: null,
  endTime: null,
})

const { jobHistory, isHistoryError } = useActivityLog(props)
const { mutateAsync: createJobComment, isLoading: isCreatingComment } =
  useCreateJobCommentMutation()
const commentDraft = ref('')
const isCommentSubmitError = ref(false)

const submitComment = async (comment: string) => {
  isCommentSubmitError.value = false
  try {
    await createJobComment({ jobId: props.jobId, comment })
    commentDraft.value = ''
  } catch {
    isCommentSubmitError.value = true
  }
}
</script>

<style scoped>
.day-container {
  margin-left: -32px;
}

.v-timeline::before {
  top: 50px;
  height: calc(100% - 100px);
}
</style>
